import { FieldNames } from '@/constants/FieldNames';
import utils from '@/utils/QSUtils';
import ILookup from '../common/ILookup';
import CompAddressUtils from '../comp/CompAddressUtils';
import ICompAddress, { newCompAddress } from '../CompAddress';
import IFactSheetProperty from './IFactSheetProperty';

export default {
    /**
     * Build a comp address from the given fact sheet property using its transfer values.
     * @param factSheetProperty
     * @returns
     */
    buildCompAddress(factSheetProperty: IFactSheetProperty): ICompAddress {
        const compAddress = newCompAddress();
        compAddress.address1 = getTransferValue(factSheetProperty, FieldNames.Address1) ?? '';
        compAddress.address2 = getTransferValue(factSheetProperty, FieldNames.Address2) ?? '';
        compAddress.unitNumber = getTransferValue(factSheetProperty, FieldNames.UnitNo) ?? '';
        compAddress.city = getTransferValue(factSheetProperty, FieldNames.City) ?? '';
        compAddress.state = getTransferValue(factSheetProperty, FieldNames.State) ?? '';
        compAddress.zipCode = getTransferValue(factSheetProperty, FieldNames.ZipCode) ?? '';
        return compAddress;
    },

    buildCompAddress36(factSheetProperty: IFactSheetProperty): ICompAddress {
        const compAddress = newCompAddress();
        if (factSheetProperty.transfer && Object.keys(factSheetProperty.transfer).some((v) => v === "SUBPROPADDRESS")) {
            compAddress.address1 = getTransferValue(factSheetProperty, "SUBPROPADDRESS") ?? '';
            compAddress.unitNumber = getTransferValue(factSheetProperty, "SUBUNITNO") ?? '';
            compAddress.city = getTransferValue(factSheetProperty, "SUBCITY") ?? '';
            compAddress.state = getTransferValue(factSheetProperty, "SUBSTATE") ?? '';
            compAddress.zipCode = getTransferValue(factSheetProperty, "SUBZIP") ?? '';      
        } else {
            compAddress.address1 = getTransferValue(factSheetProperty, "AD_ADDR1") ?? '';
            compAddress.unitNumber = getTransferValue(factSheetProperty, "DB_UNITNO") ?? '';
            compAddress.city = getTransferValue(factSheetProperty, "DB_CITY") ?? '';
            compAddress.state = getTransferValue(factSheetProperty, "DB_STATE") ?? '';
            compAddress.zipCode = getTransferValue(factSheetProperty, "DB_ZIP") ?? '';
        }

        return compAddress;
    },

    /**
     * Get an address1 and unit number string from the given fact sheet property using its transfer values.
     * @param factSheetProperty
     * @returns
     */
    combineTransferAddress1AndUnitNo(factSheetProperty: IFactSheetProperty): string {
        const address1 = getTransferAddress1(factSheetProperty);
        const unitNumber = getTransferUnitNumber(factSheetProperty);
        const result = CompAddressUtils.combineAddressAndUnitNo(address1, unitNumber);
        return result;
    },

    /**
     * Get one of the given work file property's ILookup properties by the given name.
     * A work file property has non-standard key names for some of it's properties.
     * Those key names are found in the SymbolDetail's name property.
     * @see SymbolDetailsUtil
     * @param factSheetProperty
     * @param name
     * @returns
     */
    getInnerLookupByName(factSheetProperty: IFactSheetProperty, name: string): ILookup<string | number | boolean> | null {
        if (utils.isKeyInObject(factSheetProperty, name)) {
            for (const [key, value] of Object.entries(factSheetProperty)) {
                if (key === name) {
                    return value;
                }
            }
        }

        return null;
    },
};

function getTransferAddress1(factSheetProperty: IFactSheetProperty): string | null {
    let result = null;
    if (Object.keys(factSheetProperty.transfer).some((v) => v == FieldNames.Address1)) {
        result = factSheetProperty.transfer[FieldNames.Address1];
    }
    return typeof result === 'string' ? result : null;
}

function getTransferUnitNumber(factSheetProperty: IFactSheetProperty): string | null {
    let result = null;
    if (Object.keys(factSheetProperty.transfer).some((v) => v == FieldNames.UnitNo)) {
        result = factSheetProperty.transfer[FieldNames.UnitNo];
    }
    return typeof result === 'string' ? result : null;
}

function getTransferValue(factSheetProperty: IFactSheetProperty, fieldName: string): string | null {
    let result = null;
    if (Object.keys(factSheetProperty.transfer).some((v) => v === fieldName)) {
        result = factSheetProperty.transfer[fieldName];
    }
    return result != null ? `${result}` : null;
}
